import { TablePreferenceScope } from '@/components/ui/tablePreferences';
import { ReadOnlyContext } from './readOnly';
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Capability } from '@cop/shared';
import { api, onUnauthorized, setExpectedCompany } from './api';
import { companyChangeKey } from '@/features/companies/context';
import { can as canFor, scopeOf } from './capabilities';
import { qk } from './queryClient';
import type { PublicMaintenance, SessionContext, SessionPayload } from './types';

interface SessionValue extends SessionPayload {
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refresh: () => Promise<void>;
  can: (cap: Capability) => boolean;
  scope: (cap: Capability) => ReturnType<typeof scopeOf>;
  signOut: () => Promise<void>;
  selectContext: (ctx: SessionContext) => Promise<void>;
}

const EMPTY: SessionPayload = { user: null, principal: null, contexts: [] };
const SessionCtx = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: qk.session,
    queryFn: ({ signal }) => api.get<SessionPayload>('/auth/session', undefined, { signal, silent401: true }),
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });

  // Any 401 from a data request means the session expired: drop cached data and re-bootstrap.
  useEffect(() => {
    const off = onUnauthorized(() => {
      qc.setQueryData(qk.session, EMPTY);
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== qk.session[0] });
    });
    return () => {
      off();
    };
  }, [qc]);

  const refresh = useCallback(async () => {
    await qc.invalidateQueries({ queryKey: qk.session, refetchType: 'active' });
    await qc.refetchQueries({ queryKey: qk.session });
  }, [qc]);

  const signOut = useCallback(async () => {
    // Cancel session refreshes before and after revocation so stale responses cannot restore it.
    await qc.cancelQueries();
    await api.post('/auth/logout', {}, { silent401: true });
    await qc.cancelQueries();
    qc.setQueryData(qk.session, EMPTY);
    qc.getMutationCache().clear();
    qc.removeQueries({ predicate: (q) => q.queryKey[0] !== qk.session[0] });
  }, [qc]);

  const selectContext = useCallback(
    async (ctx: SessionContext) => {
      await api.post('/auth/context', ctx.kind === 'staff' ? { tenantId: ctx.tenantId, membershipId: ctx.membershipId } : { tenantId: ctx.tenantId, participantId: ctx.participantId });
      // Context change rotates the session: nothing cached from the previous workspace may leak.
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== qk.session[0] });
      await refresh();
    },
    [qc, refresh],
  );

  const data = query.data ?? EMPTY;
  const companyAdmin = data.principal?.kind === 'staff' && ['tenant_owner', 'tenant_admin'].includes(data.principal.role) && !isPlatformOperator(data.user) && !data.impersonation;
  const previousCompany = useRef<string | null>(null);
  const companyChanged = previousCompany.current !== null && previousCompany.current !== (data.principal?.tenantId ?? null);
  useLayoutEffect(() => {
    if (companyChanged) { qc.clear(); window.location.replace('/dashboard'); return; }
    previousCompany.current = companyAdmin ? data.principal!.tenantId : null;
    setExpectedCompany(companyAdmin ? data.principal!.tenantId : null);
    return () => setExpectedCompany(null);
  }, [companyAdmin, companyChanged, data.principal?.tenantId, qc]);
  useEffect(() => {
    if (!companyAdmin) return;
    const changed = (event: StorageEvent) => {
      if (event.key !== companyChangeKey || !event.newValue) return;
      try {
        if (JSON.parse(event.newValue).userId === data.user?.id) { qc.clear(); window.location.replace('/dashboard'); }
      } catch { /* Ignore unrelated malformed storage events. */ }
    };
    window.addEventListener('storage', changed);
    return () => window.removeEventListener('storage', changed);
  }, [companyAdmin, data.user?.id, qc]);

  const value = useMemo<SessionValue>(
    () => ({
      ...data,
      isLoading: query.isPending,
      isError: query.isError,
      error: query.error,
      refresh,
      signOut,
      selectContext,
      can: (cap) => canFor(data.principal, cap),
      scope: (cap) => scopeOf(data.principal, cap),
    }),
    [data, query.isPending, query.isError, query.error, refresh, signOut, selectContext],
  );

  return <SessionCtx.Provider value={value}><ReadOnlyContext.Provider value={!!data.impersonation}><TablePreferenceScope.Provider value={`${data.user?.id ?? 'guest'}:${data.principal?.tenantId ?? 'platform'}`}>{companyChanged ? <div role="status">Loading company...</div> : children}</TablePreferenceScope.Provider></ReadOnlyContext.Provider></SessionCtx.Provider>;
}

export function useSession(): SessionValue {
  const v = useContext(SessionCtx);
  if (!v) throw new Error('useSession must be used inside <SessionProvider>');
  return v;
}

/** Convenience for components that only render once a principal exists. */
export function usePrincipal() {
  const s = useSession();
  if (!s.principal) throw new Error('usePrincipal requires a selected workspace');
  return s.principal;
}

/** Platform-level Super Admin. Independent of tenant membership; never implies tenant data access. */
export function isPlatformOperator(user: SessionPayload['user'] | undefined | null): boolean {
  return user?.platformRole === 'platform_operator';
}

/** True while a platform operator is acting as the signed-in user. UI hint only; the server enforces the limits. */
export function isImpersonating(payload: Pick<SessionPayload, 'impersonation'> | null | undefined): boolean {
  return !!payload?.impersonation;
}

/** Public maintenance state from the session payload (null when the platform does not publish it). */
export function maintenanceOf(payload: Pick<SessionPayload, 'platform'> | null | undefined): PublicMaintenance | null {
  const m = payload?.platform?.['platform.maintenance'] as Partial<PublicMaintenance> | undefined;
  if (!m || typeof m !== 'object') return null;
  return { active: !!m.active, scheduled: !!m.scheduled, message: m.message ?? '', startsAt: m.startsAt ?? null, endsAt: m.endsAt ?? null };
}

/** Whether "Continue with Google" should be offered (public flag; never includes credentials). */
export function googleLoginEnabled(payload: Pick<SessionPayload, 'platform'> | null | undefined): boolean {
  return (payload?.platform?.['auth.google'] as { enabled?: boolean } | undefined)?.enabled === true;
}

/** True only when the platform has X (Twitter) sign-in enabled with a saved client ID and secret. */
export function twitterLoginEnabled(payload: Pick<SessionPayload, 'platform'> | null | undefined): boolean {
  return (payload?.platform?.['auth.twitter'] as { enabled?: boolean } | undefined)?.enabled === true;
}

export function homePathFor(payload: Pick<SessionPayload, 'user' | 'principal' | 'contexts' | 'signupPayment'>): string {
  if (!payload.user) return '/login';
  if (payload.signupPayment) return '/signup/payment';
  // Operators without a selected workspace go to platform administration, never to the
  // workspace-membership screens. Tenant and client users keep the existing flow.
  if (!payload.principal && isPlatformOperator(payload.user)) return '/platform';
  if (!payload.principal) return payload.contexts.length ? '/workspaces' : '/no-workspace';
  return payload.principal.kind === 'client' ? '/portal' : '/dashboard';
}
